/**
 * One money formatter for the invoice surfaces.
 *
 * **The currency decides the decimals, not the caller.** Hardcoding two places
 * is right for USD and EUR and wrong for JPY (0) and KWD (3), so a ¥1,000
 * credit used to read "¥1,000.00" in the modal that raised it while the page
 * behind it read "¥1,000". `Intl` already knows each currency's minor unit —
 * asking it is both shorter and correct, which is why this takes no digit
 * options at all.
 *
 * Kept apart from `pricingCalculator.formatCurrency`, which deliberately drops
 * a trailing `.00` for catalog prices. On a financial document the minor units
 * are part of the figure: an invoice total that renders "$80" and a payment
 * that renders "$80.00" read as two different numbers.
 */

/** Resolved once per currency — `Intl.NumberFormat` construction is not free. */
const fractionDigitsByCurrency = new Map<string, number>();

/**
 * How many decimal places this currency's minor unit has (USD 2, JPY 0, KWD 3).
 *
 * Feeds the money inputs' `maxDecimals`, so an operator cannot type a fraction
 * of a yen and then watch the server round it away.
 */
export function currencyFractionDigits(currency: string): number {
  const code = (currency || 'USD').toUpperCase();
  const cached = fractionDigitsByCurrency.get(code);
  if (cached !== undefined) return cached;
  let digits = 2;
  try {
    digits =
      new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).resolvedOptions()
        .maximumFractionDigits ?? 2;
  } catch {
    // An unknown or malformed code: fall back to the common case rather than
    // throwing inside a render.
    digits = 2;
  }
  fractionDigitsByCurrency.set(code, digits);
  return digits;
}

/** A money figure with its symbol, at the currency's own precision. */
export function formatMoney(amount: number, currency: string): string {
  const code = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount);
  } catch {
    return `${amount.toFixed(currencyFractionDigits(code))} ${code}`;
  }
}

/**
 * The same figure as a bare editable string — what a money input is seeded
 * with. No symbol and no grouping, because the input adds its own.
 */
export function moneyInputValue(amount: number, currency: string): string {
  return amount.toFixed(currencyFractionDigits(currency));
}
